import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Radio, Mail, Lock, User as UserIcon } from "lucide-react";
import { useAuth, formatApiError } from "@/lib/auth";

export default function Signup() {
  const { registerEmail, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refFromUrl = params.get("ref") || "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [ageBracket, setAgeBracket] = useState("");
  const [gender, setGender] = useState("");
  const [country, setCountry] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Persist referral code so it survives the OAuth round-trip too
  useEffect(() => {
    if (refFromUrl) {
      try { sessionStorage.setItem("fliboe_ref", refFromUrl.toUpperCase()); } catch (_) {}
    }
  }, [refFromUrl]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const ref = refFromUrl || (typeof sessionStorage !== "undefined" ? sessionStorage.getItem("fliboe_ref") : null) || undefined;
      await registerEmail(email, password, name, ref, { age_bracket: ageBracket, gender, country });
      try { sessionStorage.removeItem("fliboe_ref"); } catch (_) {}
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <main className="max-w-md mx-auto px-5 pt-24 pb-16">
      <div className="text-center mb-8">
        <Radio className="w-10 h-10 text-emerald-500 mx-auto" />
        <h1 className="fl-display text-3xl font-extrabold mt-3">LAUNCH YOUR STATION</h1>
        <p className="text-slate-400 text-sm mt-1">Free to get started. Upgrade to enable the Tip Jar.</p>
        {refFromUrl && (
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 fl-mono text-[11px] tracking-widest uppercase text-emerald-500" data-testid="ref-applied-badge">
            ★ Invited by code <strong>{refFromUrl.toUpperCase()}</strong>
          </div>
        )}
      </div>

      <form onSubmit={submit} className="fl-card p-6 space-y-4">
        <label className="block">
          <span className="fl-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">Your name (or station alias)</span>
          <div className="relative mt-1.5">
            <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input required className="fl-input pl-9" value={name} onChange={e => setName(e.target.value)} data-testid="signup-name-input" placeholder="DJ Fliboe" />
          </div>
        </label>
        <label className="block">
          <span className="fl-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">Email</span>
          <div className="relative mt-1.5">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input type="email" required className="fl-input pl-9" value={email} onChange={e => setEmail(e.target.value)} data-testid="signup-email-input" placeholder="you@example.com" />
          </div>
        </label>
        <label className="block">
          <span className="fl-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">Password (min 8 chars)</span>
          <div className="relative mt-1.5">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input type="password" required minLength={8} className="fl-input pl-9" value={password} onChange={e => setPassword(e.target.value)} data-testid="signup-password-input" />
          </div>
        </label>

        <details className="group rounded-lg border border-white/10 bg-white/[0.02]" data-testid="signup-demographics">
          <summary className="cursor-pointer list-none p-3 text-xs flex items-center justify-between">
            <span className="text-slate-300">Help broadcasters know who&apos;s listening <span className="text-slate-500">(optional)</span></span>
            <span className="text-slate-500 group-open:rotate-180 transition-transform">▾</span>
          </summary>
          <div className="px-3 pb-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="fl-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">Age</span>
              <select value={ageBracket} onChange={e => setAgeBracket(e.target.value)} className="fl-input mt-1.5" data-testid="signup-age">
                <option value="">Prefer not to say</option>
                <option value="13-17">13–17</option>
                <option value="18-24">18–24</option>
                <option value="25-34">25–34</option>
                <option value="35-54">35–54</option>
                <option value="55+">55+</option>
              </select>
            </label>
            <label className="block">
              <span className="fl-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">Gender</span>
              <select value={gender} onChange={e => setGender(e.target.value)} className="fl-input mt-1.5" data-testid="signup-gender">
                <option value="">Prefer not to say</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="nonbinary">Nonbinary</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label className="block">
              <span className="fl-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">Country</span>
              <select value={country} onChange={e => setCountry(e.target.value)} className="fl-input mt-1.5" data-testid="signup-country">
                <option value="">Prefer not to say</option>
                <option value="US">United States</option>
                <option value="GB">United Kingdom</option>
                <option value="CA">Canada</option>
                <option value="AU">Australia</option>
                <option value="DE">Germany</option>
                <option value="FR">France</option>
                <option value="BR">Brazil</option>
                <option value="MX">Mexico</option>
                <option value="IN">India</option>
                <option value="PK">Pakistan</option>
                <option value="BD">Bangladesh</option>
                <option value="NG">Nigeria</option>
                <option value="ZA">South Africa</option>
                <option value="KE">Kenya</option>
                <option value="JP">Japan</option>
                <option value="PH">Philippines</option>
                <option value="ID">Indonesia</option>
                <option value="ES">Spain</option>
                <option value="IT">Italy</option>
              </select>
            </label>
          </div>
        </details>

        {error && <div className="text-sm text-red-400" data-testid="signup-error">{error}</div>}
        <div className="text-[11px] leading-relaxed text-amber-200/90 bg-amber-400/5 border border-amber-400/30 rounded-lg p-3" data-testid="signup-sharing-note">
          <strong className="text-amber-200">Heads up:</strong> Fliboe publishes your Pulse articles, station and films on Fliboe, but we don't promote or share them on social media for you. You're responsible for sharing your links to grow your audience.
        </div>
        <button type="submit" disabled={busy} className="fl-btn w-full" data-testid="signup-submit">{busy ? "Creating…" : "Create account"}</button>
        <div className="text-xs text-center text-slate-400">
          Already a broadcaster? <Link to="/login" className="text-emerald-500 hover:underline">Sign in</Link>
        </div>
      </form>

      <div className="flex items-center gap-3 my-6">
        <div className="flex-1 h-px bg-white/10" />
        <span className="fl-mono text-[10px] tracking-widest uppercase text-slate-500">or</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>
      <button onClick={loginWithGoogle} className="fl-btn ghost w-full" data-testid="google-signup-button">
        <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
        Continue with Google
      </button>
    </main>
  );
}
